import { isEmpty, uniqBy, size } from 'lodash';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';

import Button from '../../../Button';
import { Sizes as ButtonSizes } from '../../../Button/Button';
import Editor from '../../../Editor';
import AttachmentList2 from '../AttachmentList2';
import {
  LinkToolBar,
  DocumentsToolBar,
  Copilot,
} from '../../../Editor/components';
import ActionPopup from '../../Common/ActionPopup';
import Throbber from '../../../Throbber';
import Bubble from '../../../Bubble';
import UserSelect, {
  OptionTypeType as UserSelectOptionType,
  ValueTypeType as UserSelectValueType,
} from '../../Clients/UserSelect';
import { MessageType } from '../Message2';
import EmailTemplateForm, {
  EmailTemplateColumnType,
  IEmailTemplateForm,
  MailTemplatesSearchType,
  TEMPLATE_GROUPS,
} from '../EmailTemplateForm/EmailTemplateForm';
import OverlayLoader, { StyledBackground } from '../../../OverlayLoader';

import { Back } from '@icons/common';
import { Attachment } from '@icons/editor';
import { ValueOf } from '@utils/tsUtils';
import { AttachmentType } from '../Message2/Message2';
import { IUserSelect } from '@components/CRM/Clients/UserSelect/UserSelect';
import { CHAIN_SERVICES, CHAIN_FIELDS_ERRORS } from '@constants/chains';

import isEmptyHTMLMessage from '@utils/isEmptyHTMLMessage';

export const Types = {
  email: 'email',
  messenger: 'messenger',
};

export const MAX_FILE_SIZE = 10 ** 7;

export const SUGGEST_TYPES = {
  EMAILS_TO: 'emailsTo',
  EMAILS_FROM: 'emailsFrom',
};

const messengerFormLoaderStyles = {
  loaderColor: '#007AFF',
  loaderHeight: 8,
  loaderWidth: 8,
  backgroundColor: 'rgba(255, 255, 255, 1)',
};

const messengerFormEditorLoaderStyled = {
  ...messengerFormLoaderStyles,
  zIndex: 10,
};

const MessengerForm2 = ({
  values,
  buttonLabel = 'Отправить',
  type = 'email',
  subjectSuggestList = [],
  defaultFromSuggest,
  fromSuggests,
  isDisabledSubjectSelect = false,
  isDisabledFromSelect = false,
  isDisabledSubjectCopySelect = false,
  isDisabledSubjectHideCopySelect = false,
  isLoadingMailTemplate = false,
  isLoadingSuggests = false,
  isLoading = false,
  validationFields = {},
  buttonIsLoadingLabel = 'Загрузка',
  onEmailInputChange,
  onChangeSubjects = () => null,
  onChangeSubjectsCopy,
  onChangeSubjectsHideCopy,
  onChangeFrom,
  onChangeBody,
  onSubmit,
  onFocus,
  width = '340px',
  emailTemplateGroup = TEMPLATE_GROUPS.COMMON,
  emailTemplateColumns = [],
  emailTemplatesSearch,
  onSubmitEmailTemplate,
  onApplySuggestion,
  onSelectRowEmailTemplate,
  onSelectGroupEmailTemplate = () => null,
  onSearchEmailTemplates,
  onSelectFoundEmailTemplate,
  onResetEmailTemplatesSearch,
  className = '',
  from,
  subjectsCopy,
  subjectsHideCopy,
  attachmentsByOrderId = [],
  isForwardingMessage = false,
  forwardingMessage,
  isForwardingChain = false,
  forwardingChain,
  activeChainTheme = '',
  suggestions = [],
  onSetForwardingMessage = () => null,
  onResetForwardingMessage = () => null,
  onResetForwardingChain = () => null,
}: IMessengerForm2) => {
  const [isOpenSubjectsCopy, setIsOpenSubjectsCopy] = useState(
    values.isOpenSubjectsCopy
  );
  const [isOpenSubjectsHideCopy, setIsOpenSubjectsHideCopy] = useState(
    values.isOpenSubjectsHideCopy
  );
  const [body, setBody] = useState(values.body);
  const [forwardingMessageBody, setForwardingMessageBody] = useState('');
  const [attachments, setAttachments] = useState<
    React.ComponentProps<typeof AttachmentList2>['list']
  >([]);
  const [forwardAttachments, setForwardAttachments] = useState<
    AttachmentType[]
  >([]);
  const [isOpenTemplateForm, setIsOpenTemplateForm] = useState(false);
  const [isAttachmentSizeError, setIsAttachmentSizeError] = useState(false);
  const [isOpenToolbar, setIsOpenToolbar] = useState(false);
  const [isManualControlToolbar, setIsManualControlToolbar] = useState(false);
  const [isDisabledEmailToSelect, setIsDisabledEmailToSelect] = useState(false);
  const [isShownSuggestion, setIsShownSuggestions] = useState(
    !!suggestions.length
  );

  const [subjects, setSubjects] = useState(values.subjects);
  const [subjectsCopyValue, setSubjectsCopyValue] =
    useState<IMessengerForm2['subjectsCopy']>(subjectsCopy);
  const [subjectsHideCopyValue, setSubjectsHideCopyValue] =
    useState<IMessengerForm2['subjectsHideCopy']>(subjectsHideCopy);

  const [themeValue, setThemeValue] = useState<string>(
    `FWD: ${activeChainTheme}`
  );

  const [errorHints, setErrorHints] = useState<string[]>([]);

  const isForwardingProcess = isForwardingChain || isForwardingMessage;

  const uploaderRef = useRef<HTMLInputElement>(null);

  const imageUrls = values.imageUrl && [
    {
      name: 'Изображение шаблона.jpg',
      size: 0,
      url: values.imageUrl,
    },
  ];

  useEffect(() => {
    if (!!suggestions.length) setIsShownSuggestions(true);
  }, [suggestions]);

  useEffect(() => {
    setSubjectsCopyValue(subjectsCopy);
    setSubjectsHideCopyValue(subjectsHideCopy);
  }, [subjectsCopy, subjectsHideCopy]);

  useEffect(() => {
    const mandatoryFields = {
      ...validationFields,
      hasMessage: !isEmptyHTMLMessage(body),
      hasNoShownSuggestion: !isShownSuggestion,
      forwardSubjectExist: !(
        (isForwardingProcess && !subjects?.value) ||
        (isForwardingChain && !themeValue)
      ),
    };

    if (CHAIN_SERVICES.EMAIL === type)
      mandatoryFields.hasFrom = !!subjects?.value;

    const hints: string[] = Object.keys(mandatoryFields).reduce(
      (prev: string[], cur: string) => {
        if (!mandatoryFields[cur]) {
          return [...prev, CHAIN_FIELDS_ERRORS[cur] || cur];
        }

        return prev;
      },
      []
    );

    setErrorHints(hints);
  }, [validationFields, subjects, body, values.body]);

  const submitButtonTooltipContent = () => (
    <>
      Проверьте:
      {errorHints.map((hint) => (
        <div>{`• ${hint}`}</div>
      ))}
    </>
  );

  const handleSubmit = useCallback(
    (ev) => {
      ev.preventDefault();
      ev.stopPropagation();

      let submitParams = {
        attachments,
        body,
        subjects,
        imageUrl: values.imageUrl,

        from: from || defaultFromSuggest,
        subjectsCopy,
        subjectsHideCopy,
      } as Record<string, any>;

      if (isForwardingMessage)
        submitParams = {
          ...submitParams,
          subject: themeValue,
          // передаем айдишники вложений, чтобы бэк их подсосал в новой цепочке
          forwardAttachments: (
            forwardAttachments as MessageType['attachments']
          ).map((at) => at.id),
          messageId: forwardingMessage?.id,
          body: forwardingMessageBody,
          comment: body,
        };
      else if (isForwardingChain)
        submitParams = {
          subject: themeValue,
          chainId: forwardingChain,
          to: subjects?.value,
          from: from || defaultFromSuggest,
          comment: body,
          subjectsCopy,
          subjectsHideCopy,
        };

      onSubmit(submitParams);
      setBody(values.body);
      setAttachments([]);
    },
    [
      body,
      type,
      values.body,
      values.imageUrl,
      subjects,
      attachments,
      subjectsCopy,
      subjectsHideCopy,
      from,
      defaultFromSuggest,
      onSubmit,
      isForwardingChain,
      isForwardingMessage,
      forwardAttachments,
      themeValue,
    ]
  );

  const handleSubjectsChange = useCallback(
    (data) => {
      setSubjects(data);
      onChangeSubjects?.(data);
    },
    [onChangeSubjects]
  );

  const handleEmailInputChange = useCallback(
    (suggestType) => (data) => {
      onEmailInputChange(data, suggestType);
    },
    [onEmailInputChange]
  );

  const toggleIsOpenSubjectsCopy = useCallback(() => {
    setIsOpenSubjectsCopy((prev) => !prev);
  }, []);

  const toggleIsOpenSubjectsHideCopy = useCallback(() => {
    setIsOpenSubjectsHideCopy((prev) => !prev);
  }, []);

  const handleEditorChanges = useCallback(
    (editorBody, isSuggestApplied) => {
      if (editorBody !== body) {
        onChangeBody(editorBody);
      }
      if (isSuggestApplied) setIsShownSuggestions(false);
      setBody(editorBody);
    },
    [isShownSuggestion, onChangeBody]
  );

  const handleForwardEditorChange = useCallback(
    (forwardEditorBody) => {
      setForwardingMessageBody(forwardEditorBody);
    },
    [onSetForwardingMessage]
  );

  const handleThemeChange = (newThemeValue) => {
    setThemeValue(newThemeValue);
  };

  const handleUploading = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.persist();
      event.preventDefault();
      event.stopPropagation();

      const files = event.target.files;
      if (!files) return;

      let isSizeError = false;
      Array.from(files).forEach((file) => {
        if (file.size > MAX_FILE_SIZE) {
          isSizeError = true;
        }
      });

      if (!isSizeError) {
        setAttachments((prevAttachments) =>
          uniqBy([...prevAttachments, ...files], (file) => file.name)
        );
      } else {
        setIsAttachmentSizeError(true);
      }
    },
    []
  );

  const toggleIsAttachmentSizeError = useCallback(() => {
    setIsAttachmentSizeError((prev) => !prev);
  }, []);

  const toggleIsOpenTemplateForm = useCallback(() => {
    setIsOpenTemplateForm((prev) => !prev);
  }, []);

  const handleDeleteAttachment = useCallback(
    ({ attachId, name, lastModified }) => {
      if (!attachId && !name) return;

      setAttachments((prevAttachments) =>
        prevAttachments.filter((attachment) =>
          attachId
            ? attachment.id !== attachId
            : attachment.lastModified !== lastModified ||
              attachment.name !== name
        )
      );
    },
    []
  );

  const handleDeleteForwardAttachment = useCallback((id) => {
    setForwardAttachments((prevAttachments) =>
      prevAttachments.filter((attachment) => attachment.id !== id)
    );
  }, []);

  const triggerUploader = useCallback(() => {
    if (uploaderRef.current) uploaderRef.current.click();
  }, []);

  const handleSubmitAttachmentError = useCallback(() => {
    triggerUploader();
    setIsAttachmentSizeError(false);
  }, []);

  const handleFocus = useCallback(
    (ev) => {
      ev.preventDefault();

      onFocus?.();
      if (isOpenToolbar || isManualControlToolbar) return;
      setIsOpenToolbar(true);
    },
    [isOpenToolbar, isManualControlToolbar, onFocus]
  );

  const handleCustomAttachment = useCallback(
    (customAttachment) => {
      if (!customAttachment) return;

      setAttachments((prevAttachments) =>
        uniqBy([...prevAttachments, customAttachment], (file) => file.name)
      );
    },
    [attachments, setAttachments]
  );

  const toggleToolbar = useCallback(() => {
    setIsOpenToolbar(!isOpenToolbar);
    setIsManualControlToolbar(true);
  }, [isOpenToolbar]);

  const resetStates = () => {
    setSubjects(values.subjects);
    setSubjectsCopyValue(subjectsCopy);
    setSubjectsHideCopyValue(subjectsHideCopy);
    setThemeValue(`FWD: ${activeChainTheme}`);
  };

  useEffect(() => {
    setBody(values.body);
  }, [values.body]);

  useEffect(() => {
    if (forwardingMessage) {
      setForwardingMessageBody(forwardingMessage.text);
      setForwardAttachments(forwardingMessage.attachments || []);
    }
  }, [forwardingMessage]);

  useEffect(() => {
    if (isDisabledSubjectSelect) setIsDisabledEmailToSelect(true);
    if (isForwardingProcess) {
      setIsDisabledEmailToSelect(false);
      const newSubject = {
        label: `Введите Email пользователя, которому хотите переслать ${
          isForwardingChain ? 'цепочку' : 'сообщение'
        }`,
        value: '',
      };
      setSubjects(newSubject);
      setSubjectsCopyValue([]);
      setSubjectsHideCopyValue([]);
    }
    if (!isForwardingProcess) {
      resetStates();
    }
  }, [isDisabledSubjectSelect, isForwardingProcess]);

  const handleCancelForwarding = () => {
    // возвращаем предыдущие значения
    resetStates();
    setForwardingMessageBody('');
    setForwardAttachments([]);

    if (isForwardingChain) onResetForwardingChain();
    if (isForwardingMessage) onResetForwardingMessage();
  };

  const handleApplySuggestion = useCallback(
    (suggest) => {
      setBody(suggest?.text);
      setIsShownSuggestions(false);
      onApplySuggestion(suggest);
    },
    [onApplySuggestion]
  );

  const linkButton = () => <LinkToolBar />;

  const copilotButton = () => (
    <Copilot suggestions={suggestions} onApply={handleApplySuggestion} />
  );

  const documentButton = () => (
    <DocumentsToolBar
      attachmentsByOrderId={attachmentsByOrderId}
      onAttach={handleCustomAttachment}
    />
  );

  const attachmentButton = () => (
    <>
      <StyledToolbarButton
        key="attachmentButton"
        type="button"
        onClick={triggerUploader}
      >
        <StyledAttachmentIcon />
        <input
          multiple
          type="file"
          ref={uploaderRef}
          onChange={handleUploading}
        />
      </StyledToolbarButton>
      <ActionPopup
        title="Невозможно прикрепить файлы"
        description="Объем вложенных файлов не может превышать 10 МБ."
        submitText="Прикрепить другой"
        isOpen={isAttachmentSizeError}
        onCancel={toggleIsAttachmentSizeError}
        onSubmit={handleSubmitAttachmentError}
      />
    </>
  );

  const templateButton = () => (
    <>
      <StyledOverlayLoader
        isLoading={isLoadingMailTemplate}
        styles={messengerFormLoaderStyles}
      >
        <StyledToolbarButton
          key="templateButton"
          type="button"
          onClick={toggleIsOpenTemplateForm}
        >
          Шаблоны
        </StyledToolbarButton>
      </StyledOverlayLoader>
      {size(emailTemplateColumns) > 0 && (
        <EmailTemplateForm
          onClose={toggleIsOpenTemplateForm}
          isOpen={isOpenTemplateForm}
          onSubmit={onSubmitEmailTemplate}
          onSelectGroup={onSelectGroupEmailTemplate}
          onSelectRow={onSelectRowEmailTemplate}
          columns={emailTemplateColumns}
          activeTemplateGroup={emailTemplateGroup}
          search={emailTemplatesSearch}
          onSearch={onSearchEmailTemplates}
          onSelectFound={onSelectFoundEmailTemplate}
          onResetSearch={onResetEmailTemplatesSearch}
        />
      )}
    </>
  );

  const forwardForm = () => (
    <StyledEmailFields>
      {isOpenToolbar && (
        <StyledSubjectsContainer>
          <StyledEmail>
            <StyledParagraph>Тема</StyledParagraph>
            <StyledThemeInput
              $isBordered={!themeValue}
              placeholder="Тема"
              value={themeValue}
              onChange={(e) => handleThemeChange(e.target.value)}
            />
          </StyledEmail>
          <StyledEmail>
            <StyledParagraph>Кому</StyledParagraph>
            <UserSelect
              onInputChange={handleEmailInputChange(SUGGEST_TYPES.EMAILS_TO)}
              onChange={handleSubjectsChange}
              options={subjectSuggestList}
              value={subjects}
              isLoading={isLoadingSuggests}
              isDisabled={isDisabledEmailToSelect}
              isMulti={false}
              isBordered={!subjects?.value}
            />
          </StyledEmail>
          <StyledEmail>
            <StyledParagraph>От Кого</StyledParagraph>
            <StyledFormOverlayLoader
              styles={messengerFormLoaderStyles}
              isLoading={isLoading}
            >
              <UserSelect
                onChange={onChangeFrom}
                options={fromSuggests}
                value={from || defaultFromSuggest}
                isLoading={isLoadingSuggests}
                isDisabled={isDisabledFromSelect}
                isMulti={false}
                isCreatable={false}
              />
            </StyledFormOverlayLoader>
          </StyledEmail>
          {(isOpenSubjectsCopy || !isEmpty(subjectsCopy)) && (
            <StyledEmail>
              <StyledParagraph>Копия</StyledParagraph>
              <StyledFormOverlayLoader
                styles={messengerFormLoaderStyles}
                isLoading={isLoading}
              >
                <UserSelect
                  onInputChange={handleEmailInputChange(
                    SUGGEST_TYPES.EMAILS_TO
                  )}
                  onChange={onChangeSubjectsCopy}
                  options={subjectSuggestList}
                  value={subjectsCopyValue}
                  isLoading={isLoadingSuggests}
                  isDisabled={isDisabledSubjectCopySelect}
                />
              </StyledFormOverlayLoader>
            </StyledEmail>
          )}
          {(isOpenSubjectsHideCopy || !isEmpty(subjectsHideCopy)) && (
            <StyledEmail>
              <StyledParagraph>Скрытая копия</StyledParagraph>
              <StyledFormOverlayLoader
                isLoading={isLoading}
                styles={messengerFormLoaderStyles}
              >
                <UserSelect
                  onInputChange={handleEmailInputChange(
                    SUGGEST_TYPES.EMAILS_TO
                  )}
                  onChange={onChangeSubjectsHideCopy}
                  options={subjectSuggestList}
                  value={subjectsHideCopyValue}
                  isLoading={isLoadingSuggests}
                  isDisabled={isDisabledSubjectHideCopySelect}
                />
              </StyledFormOverlayLoader>
            </StyledEmail>
          )}
          {!isOpenSubjectsCopy && isEmpty(subjectsCopy) && (
            <StyledTextButton type="button" onClick={toggleIsOpenSubjectsCopy}>
              + Копия
            </StyledTextButton>
          )}
          {!isOpenSubjectsHideCopy && isEmpty(subjectsHideCopy) && (
            <StyledTextButton
              type="button"
              onClick={toggleIsOpenSubjectsHideCopy}
            >
              + Скрытая копия
            </StyledTextButton>
          )}
        </StyledSubjectsContainer>
      )}
      <StyledFormOverlayLoader
        styles={messengerFormEditorLoaderStyled}
        isLoading={isLoading}
      >
        <StyledEditor
          toolbarButtons={[
            linkButton,
            attachmentButton,
            templateButton,
            documentButton,
            copilotButton,
          ]}
          editorId="messenger-form-email"
          placeholder="Сообщение"
          outerValue={body}
          disableToolbar={!isOpenToolbar}
          onChange={handleEditorChanges}
          onFocus={handleFocus}
          suggestions={isShownSuggestion && suggestions}
          onApplySuggestion={handleApplySuggestion}
        />
        {isForwardingMessage && (
          <StyledForwardEditor
            disableToolbar
            editorId="forward-message-form"
            outerValue={forwardingMessageBody}
            onChange={handleForwardEditorChange}
          />
        )}
        {isForwardingChain && (
          <StyledForwardingChainInfo>
            * будет переслана вся цепочка сообщений *
          </StyledForwardingChainInfo>
        )}
      </StyledFormOverlayLoader>
      {attachments?.length > 0 && (
        <StyledAttachmentList
          onDelete={handleDeleteAttachment}
          list={attachments}
        />
      )}
      {isForwardingMessage && (
        <StyledAttachmentList
          isForwarding
          onDelete={handleDeleteForwardAttachment}
          list={forwardAttachments}
        />
      )}
    </StyledEmailFields>
  );

  const usualForm = () =>
    type === CHAIN_SERVICES.EMAIL ? (
      <StyledEmailFields>
        {isOpenToolbar && (
          <StyledSubjectsContainer>
            <StyledEmail>
              <StyledParagraph>Кому</StyledParagraph>
              <UserSelect
                onInputChange={handleEmailInputChange(SUGGEST_TYPES.EMAILS_TO)}
                onChange={handleSubjectsChange}
                options={subjectSuggestList}
                value={subjects}
                isLoading={isLoadingSuggests}
                isDisabled={isDisabledEmailToSelect}
                isMulti={false}
              />
            </StyledEmail>
            <StyledEmail>
              <StyledParagraph>От Кого</StyledParagraph>
              <StyledFormOverlayLoader
                styles={messengerFormLoaderStyles}
                isLoading={isLoading}
              >
                <UserSelect
                  onChange={onChangeFrom}
                  options={fromSuggests}
                  value={from || defaultFromSuggest}
                  isLoading={isLoadingSuggests}
                  isDisabled={isDisabledFromSelect}
                  isMulti={false}
                  isCreatable={false}
                />
              </StyledFormOverlayLoader>
            </StyledEmail>
            {(isOpenSubjectsCopy || !isEmpty(subjectsCopy)) && (
              <StyledEmail>
                <StyledParagraph>Копия</StyledParagraph>
                <StyledFormOverlayLoader
                  styles={messengerFormLoaderStyles}
                  isLoading={isLoading}
                >
                  <UserSelect
                    onInputChange={handleEmailInputChange(
                      SUGGEST_TYPES.EMAILS_TO
                    )}
                    onChange={onChangeSubjectsCopy}
                    options={subjectSuggestList}
                    value={subjectsCopyValue}
                    isLoading={isLoadingSuggests}
                    isDisabled={isDisabledSubjectCopySelect}
                  />
                </StyledFormOverlayLoader>
              </StyledEmail>
            )}
            {(isOpenSubjectsHideCopy || !isEmpty(subjectsHideCopy)) && (
              <StyledEmail>
                <StyledParagraph>Скрытая копия</StyledParagraph>
                <StyledFormOverlayLoader
                  isLoading={isLoading}
                  styles={messengerFormLoaderStyles}
                >
                  <UserSelect
                    onInputChange={handleEmailInputChange(
                      SUGGEST_TYPES.EMAILS_TO
                    )}
                    onChange={onChangeSubjectsHideCopy}
                    options={subjectSuggestList}
                    value={subjectsHideCopyValue}
                    isLoading={isLoadingSuggests}
                    isDisabled={isDisabledSubjectHideCopySelect}
                  />
                </StyledFormOverlayLoader>
              </StyledEmail>
            )}
            {!isOpenSubjectsCopy && isEmpty(subjectsCopy) && (
              <StyledTextButton
                type="button"
                onClick={toggleIsOpenSubjectsCopy}
              >
                + Копия
              </StyledTextButton>
            )}
            {!isOpenSubjectsHideCopy && isEmpty(subjectsHideCopy) && (
              <StyledTextButton
                type="button"
                onClick={toggleIsOpenSubjectsHideCopy}
              >
                + Скрытая копия
              </StyledTextButton>
            )}
          </StyledSubjectsContainer>
        )}
        <StyledFormOverlayLoader
          styles={messengerFormEditorLoaderStyled}
          isLoading={isLoading}
        >
          <StyledEditor
            toolbarButtons={[
              linkButton,
              attachmentButton,
              templateButton,
              documentButton,
              copilotButton,
            ]}
            editorId="messenger-form-email"
            placeholder="Сообщение"
            outerValue={body}
            disableToolbar={!isOpenToolbar}
            onChange={handleEditorChanges}
            onFocus={handleFocus}
            suggestions={isShownSuggestion && suggestions}
            onApplySuggestion={handleApplySuggestion}
          />
        </StyledFormOverlayLoader>
        {attachments?.length > 0 && (
          <StyledAttachmentList
            onDelete={handleDeleteAttachment}
            list={attachments}
          />
        )}
      </StyledEmailFields>
    ) : (
      <StyledEmailFields>
        <StyledFormOverlayLoader
          styles={messengerFormEditorLoaderStyled}
          isLoading={isLoading}
        >
          <StyledMessengerFields>
            <StyledEditor
              plain
              disableToolbar={!isOpenToolbar}
              toolbarMarks={[]}
              toolbarButtons={[
                attachmentButton,
                templateButton,
                documentButton,
                copilotButton,
              ]}
              placeholder="Сообщение"
              outerValue={body}
              editorId="messenger-form-social"
              onFocus={handleFocus}
              onChange={handleEditorChanges}
              suggestions={isShownSuggestion && suggestions}
              onApplySuggestion={handleApplySuggestion}
            />
          </StyledMessengerFields>
        </StyledFormOverlayLoader>
        {attachments?.length > 0 && (
          <StyledAttachmentList
            onDelete={handleDeleteAttachment}
            list={attachments}
          />
        )}
      </StyledEmailFields>
    );

  const submitButtonLabel = isForwardingProcess ? 'Переслать' : buttonLabel;

  return (
    <StyledMessengerForm
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      className={className}
      width={width}
    >
      {isOpenToolbar ? (
        <StyledHideToolbarButton onClick={toggleToolbar} />
      ) : (
        <StyledShowToolbarButton onClick={toggleToolbar} />
      )}

      {isForwardingProcess ? forwardForm() : usualForm()}

      {imageUrls && <StyledAttachmentList list={imageUrls} />}
      <StyledButtonsWrapper>
        <Bubble
          text={submitButtonTooltipContent()}
          placement="top"
          disabled={!errorHints.length}
        >
          <StyledSubmitButton
            onClick={handleSubmit}
            disabled={!!errorHints.length || isLoading}
            isLoading={isLoading}
            size={ButtonSizes.FLEX}
          >
            {isLoading ? (
              <>
                <StyledSpinner />
                {buttonIsLoadingLabel}
              </>
            ) : (
              submitButtonLabel
            )}
          </StyledSubmitButton>
        </Bubble>
        {isForwardingProcess && !isLoading && (
          <StyledCancelButton onClick={handleCancelForwarding}>
            Отменить
          </StyledCancelButton>
        )}
      </StyledButtonsWrapper>
    </StyledMessengerForm>
  );
};

type ValueType = {
  value?: string;
  body?: string;
  imageUrl?: string;
  subjects: UserSelectValueType | null;
  isOpenSubjectsCopy?: boolean;
  isOpenSubjectsHideCopy?: boolean;
};

export interface IMessengerForm2 {
  className?: string;
  values: ValueType;
  buttonLabel?: string;
  type?: ValueOf<typeof Types>;
  fromSuggests: UserSelectOptionType[];
  defaultFromSuggest: UserSelectOptionType;
  subjectSuggestList?: UserSelectOptionType[];
  isDisabledFromSelect?: boolean;
  isDisabledSubjectSelect?: boolean;
  isDisabledSubjectCopySelect?: boolean;
  isDisabledSubjectHideCopySelect?: boolean;
  isLoading?: boolean;
  isLoadingMailTemplate?: boolean;
  isLoadingSuggests?: boolean;
  validationFields?: any;
  buttonIsLoadingLabel?: string;
  width?: string;
  suggestions?: string[];
  emailTemplateColumns: EmailTemplateColumnType[];
  emailTemplateGroup?: string;
  emailTemplatesSearch?: MailTemplatesSearchType;
  from: UserSelectValueType;
  subjectsCopy: UserSelectValueType[];
  subjectsHideCopy: UserSelectValueType[];
  attachmentsByOrderId?: Array<{
    attachments: Array<{
      id: string;
      name: string;
      size: string;
      url: string;
    }>;
    active: boolean;
    id: string;
  }>;
  isForwardingMessage?: boolean;
  isForwardingChain?: boolean;
  forwardingMessage?: MessageType;
  forwardingChain?: string;
  activeChainTheme?: string;
  onSubmit: (submitParams: any) => void;
  onFocus?: () => void;
  onChangeBody: (body: string) => void;
  onChangeFrom: IUserSelect['onChange'];
  onChangeSubjects?: IUserSelect['onChange'];
  onChangeSubjectsCopy: IUserSelect['onChange'];
  onChangeSubjectsHideCopy: IUserSelect['onChange'];
  onEmailInputChange: (
    data: any,
    suggestType: ValueOf<typeof SUGGEST_TYPES>
  ) => void;
  onSubmitEmailTemplate: IEmailTemplateForm['onSubmit'];
  onSetForwardingMessage?: (data: any) => void;
  onResetForwardingMessage?: () => void;
  onResetForwardingChain?: () => void;
  onApplySuggestion: (data: any) => void;
  onSelectRowEmailTemplate: IEmailTemplateForm['onSelectRow'];
  onSelectGroupEmailTemplate?: IEmailTemplateForm['onSelectGroup'];
  onSearchEmailTemplates?: IEmailTemplateForm['onSearch'];
  onSelectFoundEmailTemplate?: IEmailTemplateForm['onSelectFound'];
  onResetEmailTemplatesSearch?: IEmailTemplateForm['onResetSearch'];
}

const StyledForwardingChainInfo = styled.div`
  margin: 20px 0;
  border: 1px solid #e1e3e1;
  width: 100%;
  border-radius: 10px;
  padding: 5px 10px;
`;

const StyledEditor = styled(Editor)`
  margin-bottom: 6px;
  padding: 0;

  [role='textbox'] {
    max-height: 150px;
    overflow-y: auto;
    overflow-x: hidden;
  }
`;

const StyledThemeInput = styled.input<{ $isBordered: boolean }>`
  margin-bottom: 6px;
  border-radius: 4px;
  border: ${({ $isBordered }) => ($isBordered ? '1px solid red' : 'none')};
  height: 38px;
  padding: 10px;
  width: 100%;
  outline: none;
`;

const StyledForwardEditor = styled(StyledEditor)`
  margin: 20px 0;
  border: 1px solid #e1e3e1;
  width: 100%;
  border-radius: 10px;
  padding: 5px 10px;
`;

const StyledMessengerForm = styled.form<{ width: string }>`
  position: relative;
  width: ${({ width }) => width};
  background: white;
`;

const StyledSubjectsContainer = styled.div`
  margin-bottom: 10px;
  position: relative;
`;

const StyledEmailFields = styled.div`
  position: relative;
  max-height: 400px;
`;

const StyledMessengerFields = styled.div`
  position: relative;
`;

const StyledAttachmentIcon = styled(Attachment)`
  width: 18px;
  height: 18px;
  margin-right: 2px;
  > path {
    stroke: #007aff;
  }
`;

const StyledToolbarButton = styled.button`
  outline: 0;
  border: none;
  height: 25px;
  display: flex;
  color: #007aff;
  padding: 0 5px;
  cursor: pointer;
  min-width: 25px;
  font-size: 14px;
  min-height: 25px;
  line-height: 24px;
  overflow: visible;
  text-align: center;
  position: relative;
  border-radius: 21px;
  align-items: center;
  background: transparent;
  margin-left: 5px;

  &:hover {
    color: #179fe3;
    svg > path {
      stroke: #179fe3;
    }
  }
  > input {
    display: none;
  }
`;

const StyledAttachmentList = styled(AttachmentList2)`
  margin-bottom: 10px;
  a,
  span {
    color: #4d4d4d;
    font-size: 14px;
  }
  svg > path {
    stroke: #4d4d4d;
  }
`;

const StyledEmail = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  background-color: white;

  p {
    margin-right: 10px;
  }
`;

const StyledParagraph = styled.p`
  width: 68px;
  font-family: 'Backpack', sans-serif;
  font-size: 14px;
  color: #c2c5cc;
`;

const StyledButtonsWrapper = styled.div`
  display: flex;
`;

export const StyledSubmitButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 186px;
  color: white;
  font-weight: bold;
  border-radius: 4px;
  background-color: ${({ disabled }) => (disabled ? '#c2c5cc' : '#007aff')};
  cursor: ${({ disabled, isLoading }) =>
    disabled || isLoading ? 'not-allowed' : 'pointer'};
`;

const StyledCancelButton = styled(Button)`
  margin-left: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 186px;
  font-weight: bold;
  border-radius: 4px;
  cursor: 'pointer';
`;

const StyledTextButton = styled.button`
  outline: none;
  border: none;
  cursor: pointer;
  background: transparent;
  font: 14px 'Backpack', sans-serif;
  color: #007aff;
  margin-right: 10px;

  &:last-of-type {
    margin-right: 0;
  }
`;

const StyledSpinner = styled(Throbber)`
  height: 20px;
  width: 20px;
  position: relative;
  margin: 0;
  left: 0;
  display: inline-block;

  ::before {
    width: 20px;
    height: 20px;
    bottom: 0;
    top: -1px;
    position: absolute;
  }
`;

const StyledOverlayLoader = styled(OverlayLoader)`
  ${StyledBackground} {
    left: 5px;
  }
`;

const StyledFormOverlayLoader = styled(OverlayLoader)`
  width: 100%;
`;

const StyledHideToolbarButton = styled(Back)`
  position: absolute;
  cursor: pointer;
  background: white;
  box-sizing: border-box;
  border-radius: 5px;
  top: -20px;
  left: 2px;
  padding: 2px;
  width: 20px;
  height: 20px;
  fill: #007aff;
  transform: rotate(-90deg);
`;

const StyledShowToolbarButton = styled(StyledHideToolbarButton)`
  transform: rotate(90deg);
`;

export default MessengerForm2;
